import { FC, useEffect, useMemo, useState } from "react";
import { CarDataType } from "data/types";
import { useLocation, useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import { CarListingModal } from "models/vehicleModal";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Heading from "shared/Heading/Heading";
import FleetCard from "designs/DesignA/components/FleetCard/FleetCard";
import TabFilters from "../../../Common/TabFilters";
import { hasAnyClass, inAnyClass, uniqueFleetClassNames } from "utils/fleetClasses";
import { useFilterReset } from "hooks/useFilterReset";
import NoVehiclesMatch from "designs/Common/NoVehiclesMatch";
// 1NOW-11193 (8 Oct): the price and the sort live in the URL too, in Design B's words
// (?price=0-100&sort=price-asc), so a refresh or a shared link keeps them.
import { DESIGN_A_SORT_URL as SORT_URL, designASortFromUrl as sortFromUrl } from "utils/fleetSort";

export interface SectionGridFilterCardProps {
  className?: string;
  data?: CarDataType[];
}

const SectionGridFilterCard: FC<SectionGridFilterCardProps> = ({
  className = "",
  data,
}) => {
  const { ourVehicles, ourVehiclesLoading, setSelectedCar } = useData();
  const [visibleCarsCount, setVisibleCarsCount] = useState<number>(8);
  const [expanded, setExpanded] = useState<boolean>(false);
  const location = useLocation();
  // 1NOW-11193: the filters live in the URL (one class=/brand=/model= per pick), so a link like
  // /vehicles?class=SUV opens filtered and a refresh keeps what was on screen.
  const queryParams = new URLSearchParams(location.search);
  const listParam = (key: string) => queryParams.getAll(key).map((v) => v.trim()).filter(Boolean);
  const urlClassKey = listParam("class").join("\n");
  const [selectedTypes, setSelectedTypes] = useState<string[]>(() => listParam("class"));
  const [selectedMakes, setSelectedMakes] = useState<string[]>(() => listParam("brand"));
  const [selectedModels, setSelectedModels] = useState<string[]>(() => listParam("model"));
  const [sortOrder, setSortOrder] = useState<string>(() => sortFromUrl(queryParams.get("sort")));
  const [maxPrice, setMaxPrice] = useState(0);
  const [filteredFleet, setFilteredFleet] = useState<CarListingModal[]>(
    ourVehicles || []
  );
  // The price range the renter picked, null until they do (then it is the whole range, 0 to the
  // dearest car, and follows the fleet). A link's ?price= is the first pick, so it is there before
  // the fleet is, and nothing that runs when the fleet arrives can reset it.
  const urlPrice = (queryParams.get("price") ?? "").match(/^(\d+)-(\d+)$/);
  const [pickedRange, setPickedRange] = useState<number[] | null>(
    urlPrice && Number(urlPrice[1]) <= Number(urlPrice[2]) ? [Number(urlPrice[1]), Number(urlPrice[2])] : null
  );
  const rangePrices = pickedRange ?? [0, maxPrice];
  const setRangePrices = (v: number[]) => setPickedRange(v[0] <= 0 && v[1] >= maxPrice ? null : v);
  // A price that is not the whole range. While the fleet is still on its way (maxPrice 0) a link's
  // price stays in the address.
  const priceMoved = pickedRange !== null && (maxPrice === 0 || pickedRange[0] > 0 || pickedRange[1] < maxPrice);
  const navigate = useNavigate();
  // 1NOW-11132: "Clear filters" and the no-match message.
  const { filtersActive, filtersKey, pickPrices, clearFilters } = useFilterReset({
    selectedTypes,
    selectedMakes,
    selectedModels,
    setSelectedTypes,
    setSelectedMakes,
    setSelectedModels,
    setRangePrices,
    maxPrice,
  });

  useEffect(() => {
    if (ourVehicles && ourVehicles.length > 0) {
      const calculatedMaxPrice = ourVehicles.reduce(
        (acc, current) => Math.max(acc, current.avg_car_price_per_day ?? 0),
        0
      );
      setMaxPrice(calculatedMaxPrice);
    }
  }, [ourVehicles]);

  const getUniqueFleetClasses = () => uniqueFleetClassNames(ourVehicles);

  // The URL's classes in the fleet's own spelling (?class=suv ticks "SUV"), and followed when the
  // URL changes on this page (a category link, or the Vehicles menu item clearing it).
  const fleetClasses = useMemo(() => uniqueFleetClassNames(ourVehicles), [ourVehicles]);
  useEffect(() => {
    const wanted = urlClassKey
      ? urlClassKey.split("\n").map((name) => fleetClasses.find((c) => c.toLowerCase() === name.toLowerCase()) ?? name)
      : [];
    if (wanted.join("\n") !== selectedTypes.join("\n")) setSelectedTypes(wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlClassKey, fleetClasses]);

  // And every Apply writes them back (replaced, not pushed: Back leaves the page).
  useEffect(() => {
    const next = new URLSearchParams(location.search);
    (["class", "brand", "model"] as const).forEach((key) => next.delete(key));
    selectedTypes.forEach((v) => next.append("class", v));
    selectedMakes.forEach((v) => next.append("brand", v));
    selectedModels.forEach((v) => next.append("model", v));
    if (SORT_URL[sortOrder]) next.set("sort", SORT_URL[sortOrder]);
    else next.delete("sort");
    if (priceMoved) next.set("price", `${rangePrices[0]}-${rangePrices[1]}`);
    else next.delete("price");
    const search = next.toString();
    if (search !== location.search.replace(/^\?/, "")) {
      navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypes, selectedMakes, selectedModels, sortOrder, priceMoved, rangePrices[0], rangePrices[1]]);

  const getUniqueMakes = () => {
    return Array.from(
      new Set(ourVehicles?.map((car) => car?.make ?? "") || [])
    ).filter((item): item is string => item !== undefined && item !== "");
  };

  const getUniqueModels = () => {
    return Array.from(
      new Set(ourVehicles?.map((car) => car?.model ?? "") || [])
    ).filter((item): item is string => item !== undefined && item !== "");
  };

  const hasMakeData = () => {
    return ourVehicles?.some((car) => car?.make && car.make !== "") || false;
  };

  const hasModelData = () => {
    return ourVehicles?.some((car) => car?.model && car.model !== "") || false;
  };

  useEffect(() => {
    if (ourVehicles) {
      let filtered = [...ourVehicles];
      filtered = filtered.filter(
        (item) =>
          (item.avg_car_price_per_day ?? 0) >= rangePrices[0] &&
          (item.avg_car_price_per_day ?? 0) <= rangePrices[1]
      );


      if (selectedTypes.length > 0) {
        const totalTypes = getUniqueFleetClasses().length;
        if (selectedTypes.length === totalTypes) {
          filtered = filtered.filter(hasAnyClass);
        } else {
          filtered = filtered.filter((item: CarListingModal) =>
            inAnyClass(item, selectedTypes)
          );
        }
      }

      if (selectedMakes.length > 0) {
        const totalMakes = getUniqueMakes().length;
        if (selectedMakes.length === totalMakes) {
          filtered = filtered.filter(
            (item: CarListingModal) => (item?.make ?? "").trim() !== ""
          );
        } else {
          filtered = filtered.filter((item: CarListingModal) =>
            selectedMakes.includes(item?.make ?? "")
          );
        }
      }


      if (selectedModels.length > 0) {
        const totalModels = getUniqueModels().length;
        if (selectedModels.length === totalModels) {
          filtered = filtered.filter(
            (item: CarListingModal) => (item?.model ?? "").trim() !== ""
          );
        } else {
          filtered = filtered.filter((item: CarListingModal) =>
            selectedModels.includes(item?.model ?? "")
          );
        }
      }

      if (sortOrder === "aToZ") {
        filtered.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
      } else if (sortOrder === "zToA") {
        filtered.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameB.localeCompare(nameA);
        });
      } else if (sortOrder === "priceLowToHigh") {
        filtered.sort(
          (a, b) =>
            (a.avg_car_price_per_day ?? 0) - (b.avg_car_price_per_day ?? 0)
        );
      } else if (sortOrder === "priceHighToLow") {
        filtered.sort((a, b) => (b.avg_car_price_per_day ?? 0) - (a.avg_car_price_per_day ?? 0));
      }

      setFilteredFleet(filtered);

      if (filtered.length <= visibleCarsCount) {
        setExpanded(false);
      }
    }
  }, [
    ourVehicles,
    selectedTypes,
    selectedMakes,
    selectedModels,
    rangePrices[0],
    rangePrices[1],
    sortOrder,
  ]);

  const renderCard = (car: CarListingModal) => {
    return (
      <FleetCard
        key={car.id}
        data={car}
        onClick={() => {
          navigate("/car-listing-detail?selectedCarId=" + car.id);
          setSelectedCar(car);
        }}
      />
    );
  };
  const carsToDisplay = filteredFleet?.slice(0, visibleCarsCount);
  const shouldCenter = carsToDisplay && carsToDisplay.length < 4;

  const handleShowMore = () => {
    setVisibleCarsCount((prevCount) => prevCount + 8);
    if (filteredFleet && visibleCarsCount + 8 >= filteredFleet.length) {
      setExpanded(true);
    }
  };

  const handleShowLess = () => {
    setVisibleCarsCount(8);
    setExpanded(false);
  };

  return (
    <div className="nc-SectionGridFeaturePlaces relative">
      <div className="flex flex-col mb-8 relative">
        <div className="flex justify-between">
          <div className="flex flex-col gap-1">
            <Heading>Explore All Vehicles ({filteredFleet.length})</Heading>

          </div>
          {ourVehicles && (
            <TabFilters
              key={filtersKey}
              types={[...getUniqueFleetClasses()]}
              makes={hasMakeData() ? [...getUniqueMakes()] : []}
              models={hasModelData() ? [...getUniqueModels()] : []}
              selectedTypes={selectedTypes}
              selectedMakes={selectedMakes}
              selectedModels={selectedModels}
              setSelectedTypes={setSelectedTypes}
              setSelectedMakes={setSelectedMakes}
              setSelectedModels={setSelectedModels}
              rangePrices={rangePrices}
              setRangePrices={pickPrices}
              maxPrice={maxPrice}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              showMakeFilter={hasMakeData()}
              showModelFilter={hasModelData()}
            />
          )}
        </div>
      </div>
      <div
        className={`${shouldCenter
          ? "flex flex-wrap justify-center gap-6 md:gap-8 px-4 sm:px-8 md:px-12 lg:px-12"
          : "items-stretch grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          }`}
      >
        {ourVehiclesLoading ? (
          <div className="flex justify-center items-center w-full">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-t-transparent border-secondary-100"></div>
          </div>
        ) : (filtersActive || priceMoved) && (ourVehicles?.length ?? 0) > 0 && filteredFleet.length === 0 ? (
          <NoVehiclesMatch onClear={clearFilters} />
        ) : (
          carsToDisplay?.map((car) => renderCard(car))
        )}
      </div>
      {!expanded && filteredFleet?.length > visibleCarsCount && (
        <div className="flex mt-16 justify-center items-center">
          <ButtonPrimary className="rounded-md" onClick={handleShowMore}>
            View more
          </ButtonPrimary>
        </div>
      )}
      {expanded && filteredFleet?.length > 8 && (
        <div className="flex mt-16 justify-center items-center">
          <ButtonPrimary className="rounded-md" onClick={handleShowLess}>
            View less
          </ButtonPrimary>
        </div>
      )}
    </div>
  );
};

export default SectionGridFilterCard;
